/**
 * 🗄️ Database type metadata (label + icon + short description) por tipo de base.
 * Fonte única para gallery cards, rows, empty-state e seletor de base.
 */
import type { IconName } from '@/shared/icons';

export interface DatabaseTypeMeta {
  label: string;
  icon: IconName;
  description: string;
}

/** Ordem canônica dos tipos (usada nos filtros e no empty-state). */
export const DATABASE_TYPE_ORDER = [
  'properties',
  'products',
  'services',
  'vehicles',
  'documents',
] as const;

export const DATABASE_TYPE_META: Record<string, DatabaseTypeMeta> = {
  properties: { label: 'Imóveis', icon: 'home', description: 'Catálogo imobiliário' },
  products: { label: 'Produtos', icon: 'package', description: 'Loja / catálogo' },
  services: { label: 'Serviços', icon: 'tool', description: 'Serviços prestados' },
  vehicles: { label: 'Veículos', icon: 'truck', description: 'Estoque de veículos' },
  documents: { label: 'Documentos', icon: 'file-text', description: 'Base de conhecimento' },
};

export const getDatabaseTypeMeta = (type?: string): DatabaseTypeMeta =>
  (type ? DATABASE_TYPE_META[type] : undefined) ?? {
    label: 'Base',
    icon: 'database',
    description: 'Dados estruturados',
  };

/** Rótulo do status da base (para badge). */
export const DATABASE_STATUS_LABEL: Record<string, string> = {
  active: 'Ativa',
  inactive: 'Inativa',
  archived: 'Arquivada',
};

/**
 * Marcações de uma base candidata a DESTINO de "copiar/mover registros".
 * Ordem fixa: primeiro o status (quando não está ativa), depois a integração.
 * Mesma redação dos selos do card da galeria (`DatabaseGalleryCard`).
 *
 * Base sem status conhecido não recebe marca — documento antigo pode não ter o
 * campo, e inventar "Inativa" ali seria pior que não marcar.
 */
export const getDatabaseTargetMarks = (
  database: { status?: string; hasIntegration?: boolean }
): string[] => {
  const marks: string[] = [];
  if (database.status && database.status !== 'active') {
    marks.push(DATABASE_STATUS_LABEL[database.status] ?? database.status);
  }
  if (database.hasIntegration) marks.push('Integração');
  return marks;
};

/**
 * Rótulo da opção de base de destino: nome mais as marcações entre parênteses.
 * Ex.: `Imóveis Litoral (Inativa · Integração)`.
 *
 * A marcação vira TEXTO porque o `Select` compartilhado renderiza `<option>`
 * nativo e `SelectOption.label` é `string` — não cabe selo nem ícone ali.
 */
export const buildDatabaseTargetLabel = (
  database: { name: string; status?: string; hasIntegration?: boolean }
): string => {
  const marks = getDatabaseTargetMarks(database);
  return marks.length > 0 ? `${database.name} (${marks.join(' · ')})` : database.name;
};
